import TruncatedText from "@components/Atoms/TruncatedText";
import { RightNavigationButton } from "@components/Layouts/CatalogLayout/RightNavigation/RightNavigationButton";
import { useRouter } from "@core/Api/useRouter";
import RouterPathProvider from "@core/RouterPath/RouterPathProvider";
import { useApi, useApiEvent } from "@core-ui/hooks/useApi";
import { usePlatform } from "@core-ui/hooks/usePlatform";
import { useCatalogPropsStore } from "@core-ui/stores/CatalogPropsStore/CatalogPropsStore.provider";
import type GitBranchData from "@ext/git/core/GitBranch/model/GitBranchData";
import t from "@ext/localization/locale/translate";
import { addScopeToPath } from "@ext/versioning/addScopeToPath";
import { encodeUrlPath } from "@ext/versioning/encodeUrlPath";
import {
	DropdownMenu,
	DropdownMenuContent,
	DropdownMenuRadioGroup,
	DropdownMenuRadioItem,
	DropdownMenuSeparator,
	DropdownMenuTrigger,
} from "@ui-kit/Dropdown";
import { TextOverflowTooltip } from "@ui-kit/Tooltip";
import { type ReactElement, useCallback, useEffect, useState } from "react";

interface SwitchVersionProps {
	trigger?: ReactElement;
}

const SwitchVersion = ({ trigger }: SwitchVersionProps) => {
	const { isTauri, isWeb, isStatic } = usePlatform();
	const catalogName = useCatalogPropsStore((s) => s.data.name);
	const { resolvedVersions, resolvedVersion, currentBranch } = useCatalogPropsStore(
		(state) => ({
			resolvedVersions: state.data.resolvedVersions,
			resolvedVersion: state.data.resolvedVersion,
			currentBranch: state.data.currentBranch,
		}),
		"shallow",
	);

	const {
		call: getBranch,
		data: branch,
		reset,
	} = useApi<GitBranchData>({
		url: (api) => api.getCurrentBranch(),
	});

	const [isLoading, setIsLoading] = useState(false);

	useApiEvent("on-did-command", ({ command }) => {
		if (command?.startsWith("page/")) setIsLoading(false);
	});

	// biome-ignore lint/correctness/useExhaustiveDependencies: expected
	useEffect(() => {
		// resolvedVersion changing means the switch finished — stop the spinner. The `on-did-command`
		// listener above only fires on platforms that go through the command bus (web/tauri); the
		// docportal fetches page data directly and never emits it, so rely on the store change here.
		setIsLoading(false);
		reset();
		if (!resolvedVersions?.length) return;
		// The page data carries the branch the page was built from, and a built site has no server behind the api
		// call. `null` is git having named no branch (a detached checkout), so don't ask either.
		if (currentBranch !== undefined) return;
		void getBranch();
	}, [resolvedVersion]);

	const branchName = currentBranch ?? branch?.name;
	const router = useRouter();
	const isActualVersion = !resolvedVersion;

	// biome-ignore lint/correctness/useExhaustiveDependencies: expected
	const onSwitch = useCallback(
		(name?: string) => {
			if (name === resolvedVersion?.name || (isActualVersion && name === branchName)) return;

			setIsLoading(true);
			const validatedName = name === branchName ? null : name;

			// A built site is one catalog per directory with no server behind the router, so the version
			// next to it is a neighbouring site: open it as a document instead of pushing a route this
			// bundle cannot resolve. A version sits under `versions/<ref>` from the root of the build,
			// a slash in the ref name being a directory level; `document.baseURI` is that root, so a
			// site deployed under a sub-path keeps working.
			if (isStatic) {
				const route = router.path.replace(/^\/+/, "").replace(/\/+$/, "");
				const versionDir = validatedName ? [encodeUrlPath(`versions/${validatedName}`)] : [];
				window.location.href = new URL(`${[...versionDir, route].join("/")}/`, document.baseURI).href;
				return;
			}

			const scopedPath = addScopeToPath(router.path, validatedName);

			if (!isTauri && !isWeb) {
				router.pushPath(scopedPath);
				return;
			}

			const data = RouterPathProvider.parsePath(router.path);
			const newPath = RouterPathProvider.getPathname({
				...data,
				catalogName: addScopeToPath(catalogName, validatedName),
			});
			router.pushPath(newPath.value);
		},
		[resolvedVersion, isActualVersion, branchName, router],
	);

	if (!resolvedVersions?.length) return null;

	const versions = resolvedVersions.filter((version) => version.name !== branchName);

	return (
		<DropdownMenu>
			<DropdownMenuTrigger asChild data-testid="switch-version-trigger">
				{trigger ?? (
					<RightNavigationButton
						asTrigger
						isLoading={isLoading}
						startIcon="git-compare"
						trailingIcon="chevron-down"
					>
						<TextOverflowTooltip>
							{isActualVersion ? branchName || t("versions.switch") : resolvedVersion.name}
						</TextOverflowTooltip>
					</RightNavigationButton>
				)}
			</DropdownMenuTrigger>
			<DropdownMenuContent align="start">
				<DropdownMenuRadioGroup onValueChange={onSwitch} value={resolvedVersion?.name || branchName}>
					<DropdownMenuRadioItem data-qa="qa-clickable" value={branchName}>
						<TruncatedText maxWidth={180}>{branchName || t("versions.switch")}</TruncatedText>
					</DropdownMenuRadioItem>
					{versions.length > 0 && <DropdownMenuSeparator />}
					{versions.map((version) => (
						<DropdownMenuRadioItem data-qa="qa-clickable" key={version.name} value={version.name}>
							<TruncatedText maxWidth={180}>{version.name}</TruncatedText>
						</DropdownMenuRadioItem>
					))}
				</DropdownMenuRadioGroup>
			</DropdownMenuContent>
		</DropdownMenu>
	);
};

export default SwitchVersion;
